import * as THREE from 'three';
import { AnimationClip, AnimationMixer, AnimationAction, Object3D, Group } from 'three';

export interface AnimationSpotInfo {
  id: string;
  name: string;
  spot: Object3D;
  animationIndices: number[];
  isPlaying: boolean;
  userData: any;
}

export interface AnimationInfo {
  id: string;
  name: string;
  clip: AnimationClip;
  duration: number;
  userData: any;
}

export class AnimationManager {
  private _animations: AnimationClip[] = [];
  private _mixers: Map<string, AnimationMixer> = new Map();
  private _animationSpots: Map<string, AnimationSpotInfo> = new Map();
  private _nextSpotId: number = 1;
  private _nextMixerId: number = 1;

  // 콜백 함수들
  public _onAnimationStarted: ((spotId: string, animationIndices: number[]) => void) | null = null;
  public _onAnimationStopped: ((spotId: string) => void) | null = null;
  public _onAnimationCompleted: ((spotId: string) => void) | null = null;
  public _onSpotCreated: ((spotInfo: AnimationSpotInfo) => void) | null = null;
  public _onSpotRemoved: ((spotId: string) => void) | null = null;

  constructor() {}

  /**
   * 애니메이션 클립 설정
   */
  public setAnimations(animations: AnimationClip[]): void {
    this._animations = animations;
  }

  /**
   * 애니메이션 스팟 생성
   */
  public createAnimationSpot(
    targetObject: Object3D,
    animationIndices: number[],
    name?: string,
    userData?: any,
  ): string {
    const id = this.generateSpotId();
    const spotName = name || `AnimationSpot_${id}`;

    // 애니메이션 스팟 메시 생성
    const geometry = new THREE.SphereGeometry(0.043, 32, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      opacity: 0.48,
      transparent: true,
    });

    const spotMesh = new THREE.Mesh(geometry, material);
    spotMesh.name = `AnimationSpotMesh_${spotName}`;

    // 스팟 그룹 생성
    const spotGroup = new THREE.Group();
    spotGroup.name = `AnimationSpotGroup_${spotName}`;
    spotGroup.add(spotMesh);
    spotGroup.position.set(0, 0, 0);
    spotGroup.visible = false;

    // 타겟 오브젝트에 스팟 추가
    targetObject.add(spotGroup);

    const spotInfo: AnimationSpotInfo = {
      id,
      name: spotName,
      spot: spotGroup,
      animationIndices,
      isPlaying: false,
      userData: userData || {},
    };

    this._animationSpots.set(id, spotInfo);

    if (this._onSpotCreated) {
      this._onSpotCreated(spotInfo);
    }

    return id;
  }

  /**
   * 애니메이션 스팟 제거
   */
  public removeAnimationSpot(spotId: string): boolean {
    const spotInfo = this._animationSpots.get(spotId);
    if (!spotInfo) return false;

    // 애니메이션 정지
    this.stopAnimation(spotId);

    // 스팟 오브젝트 제거
    if (spotInfo.spot.parent) {
      spotInfo.spot.parent.remove(spotInfo.spot);
    }

    // 스팟 정보 제거
    this._animationSpots.delete(spotId);

    if (this._onSpotRemoved) {
      this._onSpotRemoved(spotId);
    }

    return true;
  }

  /**
   * 애니메이션 재생
   */
  public playAnimation(spotId: string): boolean {
    const spotInfo = this._animationSpots.get(spotId);
    if (!spotInfo || spotInfo.isPlaying) return false;

    const mixerId = this.generateMixerId();
    const mixer = new THREE.AnimationMixer(spotInfo.spot);

    // 애니메이션 액션 생성 및 재생
    spotInfo.animationIndices.forEach((index) => {
      if (index >= 0 && index < this._animations.length) {
        const clip = this._animations[index];
        const action = mixer.clipAction(clip);

        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
        action.play();
      }
    });

    this._mixers.set(mixerId, mixer);
    spotInfo.isPlaying = true;

    if (this._onAnimationStarted) {
      this._onAnimationStarted(spotId, spotInfo.animationIndices);
    }

    return true;
  }

  /**
   * 애니메이션 정지
   */
  public stopAnimation(spotId: string): boolean {
    const spotInfo = this._animationSpots.get(spotId);
    if (!spotInfo || !spotInfo.isPlaying) return false;

    // 해당 스팟의 믹서들 찾기
    this._mixers.forEach((mixer, mixerId) => {
      if (mixer.getRoot() === spotInfo.spot) {
        mixer.stopAllAction();
        this._mixers.delete(mixerId);
      }
    });

    spotInfo.isPlaying = false;

    if (this._onAnimationStopped) {
      this._onAnimationStopped(spotId);
    }

    return true;
  }

  /**
   * 애니메이션 역재생
   */
  public reversePlayAnimation(spotId: string): boolean {
    const spotInfo = this._animationSpots.get(spotId);
    if (!spotInfo) return false;

    const mixerId = this.generateMixerId();
    const mixer = new THREE.AnimationMixer(spotInfo.spot);

    // 애니메이션 액션 생성 및 역재생
    spotInfo.animationIndices.forEach((index) => {
      if (index >= 0 && index < this._animations.length) {
        const clip = this._animations[index];
        const action = mixer.clipAction(clip);

        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
        action.time = action.getClip().duration; // 애니메이션의 끝에서 시작
        action.timeScale = -1; // 애니메이션을 반대로 재생
        action.play();
      }
    });

    this._mixers.set(mixerId, mixer);
    spotInfo.isPlaying = true;

    if (this._onAnimationStarted) {
      this._onAnimationStarted(spotId, spotInfo.animationIndices);
    }

    return true;
  }

  /**
   * 모든 애니메이션 역재생
   */
  public reversePlayAllAnimations(): void {
    this._animationSpots.forEach((spotInfo) => {
      if (spotInfo.isPlaying) {
        this.stopAnimation(spotInfo.id);
        this.reversePlayAnimation(spotInfo.id);
      }
    });
  }

  /**
   * 애니메이션 스팟 표시
   */
  public showAnimationSpots(): void {
    this._animationSpots.forEach((spotInfo) => {
      spotInfo.spot.visible = true;
    });
  }

  /**
   * 애니메이션 스팟 숨기기
   */
  public hideAnimationSpots(): void {
    this._animationSpots.forEach((spotInfo) => {
      spotInfo.spot.visible = false;
    });
  }

  /**
   * 애니메이션 스팟 색상 변경
   */
  public setSpotColor(spotId: string, color: number): boolean {
    const spotInfo = this._animationSpots.get(spotId);
    if (!spotInfo) return false;

    spotInfo.spot.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const material = child.material as THREE.MeshBasicMaterial;
        if (material.color) {
          material.color.setHex(color);
        }
      }
    });

    return true;
  }

  /**
   * 애니메이션 스팟 투명도 변경
   */
  public setSpotOpacity(spotId: string, opacity: number): boolean {
    const spotInfo = this._animationSpots.get(spotId);
    if (!spotInfo) return false;

    spotInfo.spot.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const material = child.material as THREE.MeshBasicMaterial;
        if (material.opacity !== undefined) {
          material.opacity = opacity;
        }
      }
    });

    return true;
  }

  /**
   * 애니메이션 믹서 업데이트
   */
  public updateMixers(deltaTime: number): void {
    this._mixers.forEach((mixer) => {
      mixer.update(deltaTime);
    });
  }

  /**
   * 애니메이션 스팟 정보 가져오기
   */
  public getAnimationSpot(spotId: string): AnimationSpotInfo | undefined {
    return this._animationSpots.get(spotId);
  }

  /**
   * 모든 애니메이션 스팟 정보 가져오기
   */
  public getAllAnimationSpots(): Map<string, AnimationSpotInfo> {
    return this._animationSpots;
  }

  /**
   * 애니메이션 클립 정보 가져오기
   */
  public getAnimationClips(): AnimationClip[] {
    return this._animations;
  }

  /**
   * 애니메이션 믹서 정보 가져오기
   */
  public getMixers(): Map<string, AnimationMixer> {
    return this._mixers;
  }

  /**
   * 모든 애니메이션 정지
   */
  public stopAllAnimations(): void {
    this._animationSpots.forEach((spotInfo) => {
      this.stopAnimation(spotInfo.id);
    });
  }

  /**
   * 모든 애니메이션 스팟 제거
   */
  public clearAllAnimationSpots(): void {
    const spotIds = Array.from(this._animationSpots.keys());
    spotIds.forEach((spotId) => {
      this.removeAnimationSpot(spotId);
    });
  }

  /**
   * 모든 리소스 정리
   */
  public dispose(): void {
    this.stopAllAnimations();
    this.clearAllAnimationSpots();

    // 믹서 정리
    this._mixers.forEach((mixer) => {
      mixer.stopAllAction();
    });
    this._mixers.clear();

    // 애니메이션 클립 정리
    this._animations = [];
  }

  /**
   * ID 생성 함수들
   */
  private generateSpotId(): string {
    return `spot_${this._nextSpotId++}`;
  }

  private generateMixerId(): string {
    return `mixer_${this._nextMixerId++}`;
  }
}
